import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SupervisorAccount as CoordinatorIcon,
  School as BatchIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

// In production, use relative URLs (nginx proxies to backend)
const isProduction = process.env.NODE_ENV === 'production';
const API_URL = process.env.REACT_APP_API_URL || (isProduction ? '' : 'http://localhost:5000');

const Coordinators = () => {
  const [coordinators, setCoordinators] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    batch_ids: []
  });

  useEffect(() => {
    fetchCoordinators();
    fetchBatches();
  }, []);

  const fetchCoordinators = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/admin/coordinators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoordinators(response.data.coordinators || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading coordinators');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/admin/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data.batches || []);
    } catch (err) {
      console.error('Error fetching batches:', err);
    }
  };

  const handleOpenDialog = (coordinator = null) => {
    if (coordinator) {
      setEditMode(true);
      setSelectedCoordinator(coordinator);
      setFormData({
        email: coordinator.email || '',
        password: '', // Don't populate password for security
        full_name: coordinator.full_name || '',
        phone: coordinator.phone || '',
        batch_ids: coordinator.assigned_batches?.map(b => b.id) || []
      });
    } else {
      setEditMode(false);
      setSelectedCoordinator(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        phone: '',
        batch_ids: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedCoordinator(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      phone: '',
      batch_ids: []
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.email || !formData.full_name) {
        toast.error('Email and full name are required');
        return;
      }

      if (!editMode && !formData.password) {
        toast.error('Password is required for new coordinator');
        return;
      }

      const token = localStorage.getItem('access_token');
      
      if (editMode) {
        await axios.put(`${API_URL}/api/admin/coordinators/${selectedCoordinator.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coordinator updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/coordinators`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coordinator created successfully');
      }

      handleCloseDialog();
      fetchCoordinators();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving coordinator');
    }
  };

  const handleDelete = async (coordinatorId) => {
    if (!window.confirm('Are you sure you want to delete this coordinator?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_URL}/api/admin/coordinators/${coordinatorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coordinator deleted successfully');
      fetchCoordinators();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting coordinator');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CoordinatorIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight={700}>
            Student Coordinators
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Coordinator
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Assigned Batches</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coordinators.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1" color="text.secondary" py={4}>
                    No coordinators found. Add your first coordinator to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              coordinators.map((coordinator) => (
                <TableRow key={coordinator.id} hover>
                  <TableCell>{coordinator.full_name}</TableCell>
                  <TableCell>{coordinator.email}</TableCell>
                  <TableCell>{coordinator.phone || '-'}</TableCell>
                  <TableCell>
                    {coordinator.assigned_batches && coordinator.assigned_batches.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {coordinator.assigned_batches.map((batch) => (
                          <Chip
                            key={batch.id}
                            label={batch.name}
                            size="small"
                            icon={<BatchIcon />}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No batches assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(coordinator)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(coordinator.id)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Coordinator' : 'Add New Coordinator'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              fullWidth
            />
            
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            
            <TextField
              label={editMode ? "Password (leave blank to keep current)" : "Password"}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editMode}
              fullWidth
            />
            
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Assign Batches</InputLabel>
              <Select
                multiple
                value={formData.batch_ids}
                onChange={(e) => setFormData({ ...formData, batch_ids: e.target.value })}
                input={<OutlinedInput label="Assign Batches" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const batch = batches.find(b => b.id === value);
                      return batch ? (
                        <Chip key={value} label={batch.name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    <Checkbox checked={formData.batch_ids.indexOf(batch.id) > -1} />
                    <ListItemText primary={batch.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Coordinators;
